// ANN-3/4: Dimension tool. Clicks capture ENTITY anchors from the snap result (that's
// the associativity), auto-orient h/v/aligned from the AB direction, radius/diameter
// when the first click lands on a circle/arc (Alt = diameter).
import { angleOf, dist, normalizeDeg, sub, type Vec } from "../core/geometry";
import { newId } from "../doc/document";
import type { DimAnchor, DimensionEntity, Doc } from "../doc/entities";
import type { SnapResult } from "../engine/snap";
import { renderDimension } from "../engine/renderAnnotation";
import type { RenderView } from "../engine/renderEntities";
import type { Tool, ToolCtx, ToolKeyEvent, ToolPointerEvent } from "./types";

/** Map a snap result to a DimAnchor — entity anchors only for exact geometric points. */
export function anchorFromSnap(doc: Doc, snap: SnapResult): DimAnchor {
  const free: DimAnchor = { kind: "free", p: { ...snap.point } };
  if (!snap.entityId) return free;
  const entity = doc.entities[snap.entityId];
  if (!entity) return free;
  if (snap.kind === "center" && (entity.type === "circle" || entity.type === "arc")) {
    return { kind: "entity", entityId: entity.id, anchor: "center" };
  }
  if (snap.kind === "endpoint") {
    if (entity.type === "polyline") {
      const i = entity.points.findIndex((p) => dist(p, snap.point) < 1e-6);
      if (i >= 0) return { kind: "entity", entityId: entity.id, anchor: "vertex", index: i };
    }
    if (entity.type === "arc") {
      const rad = (deg: number): Vec => ({
        x: entity.center.x + entity.r * Math.cos((deg * Math.PI) / 180),
        y: entity.center.y + entity.r * Math.sin((deg * Math.PI) / 180)
      });
      if (dist(rad(entity.startDeg), snap.point) < 1e-6) {
        return { kind: "entity", entityId: entity.id, anchor: "start" };
      }
      if (dist(rad(entity.startDeg + entity.sweepDeg), snap.point) < 1e-6) {
        return { kind: "entity", entityId: entity.id, anchor: "end" };
      }
    }
  }
  return free;
}

/** ANN-3 auto-orient: near-horizontal AB → "h", near-vertical → "v", else aligned. */
export function autoOrient(a: Vec, b: Vec, forceAligned: boolean): "h" | "v" | "aligned" {
  if (forceAligned) return "aligned";
  const deg = normalizeDeg(angleOf(sub(b, a))) % 180;
  if (deg <= 15 || deg >= 165) return "h";
  if (Math.abs(deg - 90) <= 15) return "v";
  return "aligned";
}

/** Signed offset of the cursor from AB for the chosen orientation. */
export function offsetFor(orient: "h" | "v" | "aligned", a: Vec, b: Vec, cursor: Vec): number {
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  if (orient === "h") return cursor.y - mid.y;
  if (orient === "v") return cursor.x - mid.x;
  const d = dist(a, b);
  if (d < 1e-9) return 0;
  const u = { x: (b.x - a.x) / d, y: (b.y - a.y) / d };
  return (cursor.x - a.x) * -u.y + (cursor.y - a.y) * u.x;
}

type State =
  | { step: "a" }
  | { step: "b"; a: DimAnchor; aPoint: Vec }
  | { step: "place"; a: DimAnchor; b: DimAnchor; aPoint: Vec; bPoint: Vec }
  | { step: "radiusPlace"; a: DimAnchor; kind: "radius" | "diameter" };

export class DimensionTool implements Tool {
  readonly id = "dimension" as const;
  private state: State = { step: "a" };
  private cursor: Vec | null = null;
  private shiftHeld = false;

  constructor(private ctx: ToolCtx) {}

  activate(): void {
    this.reset();
  }

  deactivate(): void {
    this.reset();
  }

  cancel(): void {
    this.reset();
  }

  inProgress(): boolean {
    return this.state.step !== "a";
  }

  private reset(): void {
    this.state = { step: "a" };
    this.ctx.setSnapBase(null);
    this.ctx.setMeasure("none", "");
    this.ctx.setHint("Dimension: click the first point — or click a circle/arc for a radius (Alt = ⌀)");
    this.ctx.setCursorTip(null);
    this.ctx.requestRender();
  }

  pointerDown(e: ToolPointerEvent): void {
    if (e.button !== 0) return;
    const doc = this.ctx.store.doc;
    switch (this.state.step) {
      case "a": {
        // radius/diameter when the click lands on a circle/arc (ANN-4)
        const target = e.snap.entityId ? doc.entities[e.snap.entityId] : undefined;
        if (target && (target.type === "circle" || target.type === "arc") && e.snap.kind !== "endpoint") {
          this.state = {
            step: "radiusPlace",
            a: { kind: "entity", entityId: target.id, anchor: "center" },
            kind: e.alt ? "diameter" : "radius"
          };
          // anchor ortho/rays on the centre so Shift placement works (SNP-5)
          this.ctx.setSnapBase(target.center);
          this.ctx.setHint("Dimension: click where the value should sit");
          this.ctx.setCursorTip("place the value");
          return;
        }
        const a = anchorFromSnap(doc, e.snap);
        this.state = { step: "b", a, aPoint: e.point };
        this.ctx.setSnapBase(e.point);
        this.ctx.setHint("Dimension: click the second point");
        this.ctx.setCursorTip("second point");
        return;
      }
      case "b": {
        if (dist(e.point, this.state.aPoint) < 1e-9) return; // EC-ANN-5
        const b = anchorFromSnap(doc, e.snap);
        this.state = { step: "place", a: this.state.a, b, aPoint: this.state.aPoint, bPoint: e.point };
        this.ctx.setHint("Dimension: move to set the side, click to place · Shift = force aligned");
        this.ctx.setCursorTip("pick a side · click places");
        return;
      }
      case "place": {
        const s = this.state;
        const orient = autoOrient(s.aPoint, s.bPoint, e.shift);
        const entity: DimensionEntity = {
          id: newId(),
          type: "dimension",
          layerId: this.ctx.activeLayerId(),
          locked: false,
          hidden: false,
          a: s.a,
          b: s.b,
          offset: offsetFor(orient, s.aPoint, s.bPoint, e.point),
          orient,
          kind: "linear"
        };
        this.ctx.store.dispatch({
          kind: "addEntities",
          entities: [entity],
          z: [{ layerId: entity.layerId, index: -1 }]
        });
        this.reset(); // repeat (TL-6)
        return;
      }
      case "radiusPlace": {
        const entity: DimensionEntity = {
          id: newId(),
          type: "dimension",
          layerId: this.ctx.activeLayerId(),
          locked: false,
          hidden: false,
          a: this.state.a,
          b: { kind: "free", p: { ...e.point } },
          offset: 0,
          orient: "aligned",
          kind: this.state.kind
        };
        this.ctx.store.dispatch({
          kind: "addEntities",
          entities: [entity],
          z: [{ layerId: entity.layerId, index: -1 }]
        });
        this.reset();
        return;
      }
    }
  }

  pointerMove(e: ToolPointerEvent): void {
    this.cursor = e.point;
    this.shiftHeld = e.shift;
    this.ctx.requestRender();
  }

  pointerUp(): void {}

  doubleClick(): void {}

  key(e: ToolKeyEvent): boolean {
    if (e.key === "Escape" && this.state.step !== "a") {
      this.reset();
      return true;
    }
    return false;
  }

  measurement(): boolean {
    return false;
  }

  renderDynamic(v: RenderView): void {
    if (!this.cursor) return;
    const doc = this.ctx.store.doc;
    const s = this.state;
    let preview: DimensionEntity | null = null;
    if (s.step === "place") {
      const orient = autoOrient(s.aPoint, s.bPoint, this.shiftHeld);
      preview = {
        id: "__preview",
        type: "dimension",
        layerId: this.ctx.activeLayerId(),
        locked: false,
        hidden: false,
        a: s.a,
        b: s.b,
        offset: offsetFor(orient, s.aPoint, s.bPoint, this.cursor),
        orient,
        kind: "linear"
      };
    } else if (s.step === "radiusPlace") {
      preview = {
        id: "__preview",
        type: "dimension",
        layerId: this.ctx.activeLayerId(),
        locked: false,
        hidden: false,
        a: s.a,
        b: { kind: "free", p: { ...this.cursor } },
        offset: 0,
        orient: "aligned",
        kind: s.kind
      };
    } else if (s.step === "b") {
      const { ctx } = v;
      ctx.strokeStyle = "#8ab4f8";
      ctx.lineWidth = 1 / v.cam.scale;
      ctx.setLineDash([3 / v.cam.scale, 3 / v.cam.scale]);
      ctx.beginPath();
      ctx.moveTo(s.aPoint.x, s.aPoint.y);
      ctx.lineTo(this.cursor.x, this.cursor.y);
      ctx.stroke();
      ctx.setLineDash([]);
      return;
    }
    if (preview) renderDimension(v, preview, doc, undefined);
  }
}
